import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, HelpCircle, Trophy, Clock, Sparkles, Send, MessageCircle, Lock } from 'lucide-react';
import { recessAPI, questsAPI } from '../api';
import { toast } from 'sonner';

// Room icon mapper
const ROOM_ICONS = {
  book: BookOpen,
  help: HelpCircle,
  trophy: Trophy
};

// Emoji tooltip descriptions for children
const EMOJI_TOOLTIPS = {
  '⭐': 'Celebrate this moment',
  '🌟': 'Add excitement to the story',
  '💡': 'Share an idea',
  '🎨': 'Add creativity',
  '🎵': 'Add music or rhythm',
  '❤️': 'Show love',
  '😊': 'That made me smile',
  '👏': 'Great job!',
  '🔥': 'So exciting!',
  '💎': 'A treasure moment',
  '🌈': 'Magical!',
  '🦋': 'Beautiful!',
  '🌸': 'So pretty',
  '✨': 'Sparkly!'
};

// Emoji reactions component with tooltips
const EmojiReactions = ({ emojis, reactions = {}, onReact }) => {
  const [hoveredEmoji, setHoveredEmoji] = useState(null);
  
  return (
    <div className="flex gap-1 flex-wrap relative">
      {emojis.slice(0, 8).map(emoji => {
        const count = reactions[emoji] || 0;
        const tooltip = EMOJI_TOOLTIPS[emoji] || 'React to this';
        
        return (
          <div key={emoji} className="relative">
            <button
              onClick={() => onReact(emoji)}
              onMouseEnter={() => setHoveredEmoji(emoji)}
              onMouseLeave={() => setHoveredEmoji(null)}
              className={`px-2 py-1 rounded-full text-sm transition-all hover:scale-110 ${
                count > 0 
                  ? 'bg-yellow-500/20 border border-yellow-500/30' 
                  : 'bg-slate-800/50 hover:bg-slate-700/50'
              }`}
              data-testid={`emoji-reaction-${emoji}`}
            >
              {emoji} {count > 0 && <span className="text-xs text-yellow-400">{count}</span>}
            </button>
            {/* Tooltip */}
            {hoveredEmoji === emoji && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 whitespace-nowrap z-10 shadow-lg">
                {tooltip}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Preset phrase selector
const PhraseSelector = ({ phrases, onSelect, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  
  const categories = useMemo(() => ({
    greetings: phrases.filter(p => ['Hello', 'Great', 'Well', 'Keep', 'Thank', "You're"].some(w => p.startsWith(w))),
    collaboration: phrases.filter(p => ['Can you', "Let's", 'What do', 'Should', 'I have'].some(w => p.startsWith(w))),
    learning: phrases.filter(p => ['I finished', 'I learned', 'This is', 'I understand'].some(w => p.startsWith(w))),
    reactions: phrases.filter(p => ['Wow', 'Cool', 'Nice', 'Yes'].some(w => p.startsWith(w))),
    story: phrases.filter(p => ['What happens', 'I love this', 'Let me', 'Great beginning'].some(w => p.startsWith(w))),
    question: phrases.filter(p => ['I wonder', 'Can someone', 'I know', 'Good question'].some(w => p.startsWith(w)))
  }), [phrases]);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setExpanded(!expanded)}
        disabled={disabled}
        className="text-sm text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
      >
        {expanded ? '▼ Hide phrases' : '▶ Choose a phrase'}
      </button>
      
      {expanded && (
        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-900/50 rounded-xl">
          {Object.entries(categories).map(([category, categoryPhrases]) => (
            categoryPhrases.length > 0 && (
              <div key={category} className="col-span-2">
                <p className="text-xs text-slate-500 uppercase mb-1">{category}</p>
                <div className="flex flex-wrap gap-1">
                  {categoryPhrases.map(phrase => (
                    <button
                      key={phrase}
                      onClick={() => {
                        onSelect(phrase);
                        setExpanded(false);
                      }}
                      disabled={disabled}
                      className="px-2 py-1 text-xs bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 rounded-lg transition-all disabled:opacity-50"
                    >
                      {phrase}
                    </button>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

// Story Circle Room Component - Enhanced with story visibility and tooltips
const StoryCircleRoom = ({ childId, childName, emojis, phrases, onLeave }) => {
  const [contributions, setContributions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedPhrase, setSelectedPhrase] = useState('');
  const [showFullStory, setShowFullStory] = useState(false);
  const [myContributions, setMyContributions] = useState([]);
  const storyEndRef = React.useRef(null);

  useEffect(() => {
    loadStory();
    const interval = setInterval(loadStory, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  // Track my contributions
  useEffect(() => {
    if (childName && contributions.length > 0) {
      setMyContributions(contributions.filter(c => c.child_name === childName));
    }
  }, [contributions, childName]);

  const loadStory = async () => {
    try {
      const res = await recessAPI.getCurrentStory();
      setContributions(res.data.contributions || []);
      setPrompts(res.data.prompts || []);
    } catch (error) {
      console.error('Error loading story:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContribute = async (type, content) => {
    if (!content.trim()) return;
    
    setSending(true);
    try {
      const res = await recessAPI.contribute(childId, 'story_circle', type, content);
      if (res.data.warning) {
        toast.warning(res.data.warning);
      } else if (res.data.error) {
        toast.error(res.data.error);
        if (res.data.access_suspended) {
          onLeave();
        }
      } else {
        toast.success('Added to the story!');
        setSelectedPhrase('');
        loadStory();
        // Scroll to end after adding
        setTimeout(() => {
          storyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      toast.error('Could not add to story');
    } finally {
      setSending(false);
    }
  };

  const handleReact = async (contributionId, emoji) => {
    try {
      await recessAPI.react(contributionId, emoji);
      loadStory();
    } catch (error) {
      console.error('Error reacting:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-amber-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Loading Story Circle...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Story Info Header */}
      <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📖</span>
          <div>
            <h3 className="text-amber-300 font-semibold text-sm">Story Circle</h3>
            <p className="text-slate-400 text-xs">{contributions.length} contribution{contributions.length !== 1 ? 's' : ''} so far</p>
          </div>
        </div>
        {contributions.length > 0 && (
          <button
            onClick={() => setShowFullStory(!showFullStory)}
            className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg text-sm transition-all"
            data-testid="toggle-full-story-btn"
          >
            {showFullStory ? '📕 Collapse' : '📖 Read Full Story'}
          </button>
        )}
      </div>

      {/* Full Story View (Expanded) */}
      {showFullStory && contributions.length > 0 && (
        <div className="bg-gradient-to-br from-amber-900/20 to-slate-900/40 border border-amber-500/20 rounded-xl p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3 border-b border-amber-500/20 pb-2">
            <span className="text-lg">📜</span>
            <h4 className="text-amber-300 font-semibold">The Complete Story</h4>
          </div>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-200 leading-relaxed">
              {contributions.map((c, i) => (
                <span key={c.id}>
                  <span 
                    className={c.child_name === childName ? 'text-cyan-300 font-medium' : ''}
                    title={`Added by ${c.child_name}`}
                  >
                    {c.content}
                  </span>
                  {i < contributions.length - 1 && ' '}
                </span>
              ))}
            </p>
          </div>
          <p className="text-slate-500 text-xs mt-3">
            💡 Your contributions are highlighted in blue
          </p>
        </div>
      )}

      {/* My Contributions Summary */}
      {myContributions.length > 0 && !showFullStory && (
        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm">✏️</span>
            <p className="text-cyan-300 text-sm font-medium">Your contributions ({myContributions.length})</p>
          </div>
          <div className="space-y-1">
            {myContributions.slice(-3).map((c, i) => (
              <p key={c.id} className="text-slate-300 text-xs pl-5 truncate" title={c.content}>
                "{c.content}"
              </p>
            ))}
            {myContributions.length > 3 && (
              <p className="text-slate-500 text-xs pl-5">...and {myContributions.length - 3} more</p>
            )}
          </div>
        </div>
      )}

      {/* Story Thread - Collapsible recent entries */}
      <div className={`space-y-3 overflow-y-auto transition-all ${showFullStory ? 'max-h-48' : 'max-h-[400px]'}`}>
        {/* Story starters */}
        {contributions.length === 0 && prompts.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🌱</span>
              <p className="text-amber-300 font-medium">Start a new story!</p>
            </div>
            <p className="text-slate-400 text-sm mb-3">Choose a story seed to begin:</p>
            <div className="space-y-2">
              {prompts.map(prompt => (
                <button
                  key={prompt.id}
                  onClick={() => handleContribute('story', prompt.text)}
                  disabled={sending}
                  className="w-full text-left p-3 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 rounded-lg text-sm transition-all disabled:opacity-50 hover:scale-[1.01]"
                  data-testid={`story-prompt-${prompt.id}`}
                >
                  "{prompt.text}"
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Individual contributions */}
        {contributions.map((contribution, index) => (
          <div 
            key={contribution.id}
            className={`p-4 rounded-xl transition-all ${
              contribution.child_name === childName
                ? 'bg-cyan-500/10 border border-cyan-500/20'
                : 'bg-slate-900/50 border border-slate-800/50'
            }`}
            data-testid={`contribution-${index}`}
          >
            <div className="flex items-start gap-3 mb-2">
              {/* Contribution number badge */}
              <div className="flex flex-col items-center">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  contribution.child_name === childName 
                    ? 'bg-cyan-500/30 text-cyan-300' 
                    : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {index + 1}
                </div>
                {index < contributions.length - 1 && (
                  <div className="w-0.5 h-4 bg-slate-700 mt-1"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold text-sm ${
                    contribution.child_name === childName ? 'text-cyan-300' : 'text-white'
                  }`}>
                    {contribution.child_name}
                    {contribution.child_name === childName && (
                      <span className="text-xs text-cyan-400/60 ml-1">(you)</span>
                    )}
                  </span>
                  <span className="text-slate-600 text-xs">
                    {new Date(contribution.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-slate-300 mt-1 text-sm leading-relaxed">{contribution.content}</p>
              </div>
            </div>
            <div className="pl-9">
              <EmojiReactions 
                emojis={emojis} 
                reactions={contribution.reactions}
                onReact={(emoji) => handleReact(contribution.id, emoji)}
              />
            </div>
          </div>
        ))}
        <div ref={storyEndRef} />
      </div>

      {/* Add to story - Enhanced */}
      {contributions.length > 0 && (
        <div className="p-4 bg-gradient-to-br from-slate-900/50 to-slate-800/30 border border-slate-700/50 rounded-xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">✨</span>
            <p className="text-slate-300 font-medium">Continue the story:</p>
          </div>
          
          {/* Current story ending reminder */}
          {contributions.length > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-2 border-l-2 border-amber-500/50">
              <p className="text-slate-400 text-xs mb-1">Story so far ends with:</p>
              <p className="text-slate-300 text-sm italic">
                "...{contributions[contributions.length - 1].content.slice(-80)}"
              </p>
            </div>
          )}
          
          <PhraseSelector 
            phrases={phrases}
            onSelect={setSelectedPhrase}
            disabled={sending}
          />
          
          {selectedPhrase && (
            <div className="flex items-center gap-2 animate-fade-in">
              <p className="flex-1 text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-3 py-2 rounded-lg text-sm">
                "{selectedPhrase}"
              </p>
              <button
                onClick={() => setSelectedPhrase('')}
                className="p-2 text-slate-400 hover:text-white"
                title="Clear selection"
              >
                ✕
              </button>
              <button
                onClick={() => handleContribute('story', selectedPhrase)}
                disabled={sending}
                className="p-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg disabled:opacity-50 transition-all hover:scale-105"
                data-testid="add-to-story-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Question Garden Room Component - AI-Powered with Aret'Hera
const QuestionGardenRoom = ({ childId, childName, emojis, phrases, onLeave }) => {
  const [entries, setEntries] = useState([]);
  const [wisdomSeeds, setWisdomSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [question, setQuestion] = useState('');
  const [activeTab, setActiveTab] = useState('garden'); // 'garden' or 'wisdom'
  const [typingTimeout, setTypingTimeout] = useState(null);

  useEffect(() => {
    loadHistory();
    loadWisdomSeeds();
    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, []);

  const loadHistory = async () => {
    try {
      const res = await recessAPI.getQuestionHistory(childId, 20);
      setEntries(res.data?.entries || []);
    } catch (error) {
      console.error('Error loading question history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWisdomSeeds = async () => {
    try {
      const res = await recessAPI.getGardenOfWisdom(childId);
      setWisdomSeeds(res.data?.wisdom_seeds || []);
    } catch (error) {
      console.error('Error loading wisdom seeds:', error);
    }
  };

  const handleAskQuestion = async () => {
    if (!question.trim() || question.length < 5) {
      toast.error('Please type a longer question');
      return;
    }
    
    if (question.length > 200) {
      toast.error('Question is too long. Keep it under 200 characters.');
      return;
    }
    
    setSending(true);
    try {
      const res = await recessAPI.askQuestion(childId, question);
      
      if (res.data) {
        // Add the new entry to the list
        const newEntry = {
          id: Date.now().toString(),
          child_name: childName,
          content: res.data.question,
          ai_response: res.data.answer,
          speaker: res.data.speaker,
          created_at: new Date().toISOString(),
          is_favorite: false
        };
        setEntries(prev => [...prev, newEntry]);
        setQuestion('');
        toast.success('Aret\'Hera has answered!');
      }
    } catch (error) {
      toast.error('Could not plant your question. Try again!');
    } finally {
      setSending(false);
    }
  };

  const handleToggleFavorite = async (entryId) => {
    try {
      const res = await recessAPI.toggleFavorite(entryId);
      if (res.data) {
        // Update local state
        setEntries(prev => prev.map(e => 
          e.id === entryId ? { ...e, is_favorite: res.data.is_favorite } : e
        ));
        
        // Refresh wisdom seeds
        loadWisdomSeeds();
        
        toast.success(res.data.message);
      }
    } catch (error) {
      toast.error('Could not save to wisdom garden');
    }
  };

  const handleReact = async (entryId, emoji) => {
    try {
      await recessAPI.react(entryId, emoji);
      // Refresh to get updated reactions
      loadHistory();
    } catch (error) {
      console.error('Error reacting:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-green-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Opening the Question Garden...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 p-1 bg-slate-900/50 rounded-xl">
        <button
          onClick={() => setActiveTab('garden')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'garden' 
              ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span>🌱</span>
          <span>Question Garden</span>
        </button>
        <button
          onClick={() => setActiveTab('wisdom')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'wisdom' 
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' 
              : 'text-slate-400 hover:text-white'
          }`}
          data-testid="wisdom-tab"
        >
          <span>✨</span>
          <span>Garden of Wisdom</span>
          {wisdomSeeds.length > 0 && (
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full">
              {wisdomSeeds.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'garden' && (
        <>
          {/* Aret'Hera Visual Presence - Celestial Guide */}
          <div className="relative bg-gradient-to-br from-amber-900/20 via-emerald-900/20 to-slate-900/30 border border-amber-500/20 p-5 rounded-2xl overflow-hidden">
            {/* Celestial glow effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl"></div>
            <div className="absolute top-2 right-4 w-2 h-2 bg-amber-400/60 rounded-full animate-pulse"></div>
            <div className="absolute top-6 right-12 w-1 h-1 bg-amber-300/40 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute top-3 left-8 w-1.5 h-1.5 bg-emerald-400/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            <div className="relative flex items-center gap-4">
              {/* Aret'Hera Avatar - Glowing silhouette with Egyptian motifs */}
              <div className="relative flex-shrink-0">
                {/* Outer glow ring */}
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-gradient-to-tr from-amber-400/30 to-emerald-400/20 animate-pulse blur-sm"></div>
                {/* Main avatar container */}
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/30 via-amber-600/20 to-emerald-600/20 flex items-center justify-center border border-amber-400/30 shadow-[0_0_20px_rgba(251,191,36,0.3)]">
                  {/* Stylized face silhouette */}
                  <div className="relative">
                    {/* Crown/headdress hint */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-2 bg-amber-400/60 rounded-t-full"></div>
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-amber-300 rounded-full shadow-[0_0_8px_rgba(251,191,36,0.8)]"></div>
                    {/* Face */}
                    <div className="w-8 h-9 bg-gradient-to-b from-amber-300/80 to-amber-400/60 rounded-full relative">
                      {/* Eyes - calm, wise */}
                      <div className="absolute top-3 left-1.5 w-1.5 h-0.5 bg-slate-800/80 rounded-full"></div>
                      <div className="absolute top-3 right-1.5 w-1.5 h-0.5 bg-slate-800/80 rounded-full"></div>
                      {/* Serene smile */}
                      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 w-2 h-0.5 bg-slate-700/60 rounded-full"></div>
                    </div>
                  </div>
                </div>
                {/* Lotus/scarab accent */}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-amber-400 text-xs">❋</div>
              </div>
              
              {/* Welcome text */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-amber-300 font-philosopher font-bold text-lg">Aret'Hera</span>
                  <span className="text-emerald-400/60 text-xs italic">Guardian of the Garden</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Welcome, young seeker. I am here to help your curiosity bloom into wisdom. 
                  Plant your questions as seeds, and watch knowledge grow.
                </p>
              </div>
            </div>
          </div>

          {/* Question/Answer history */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {entries.length === 0 && (
              <div className="text-center py-8">
                <span className="text-4xl mb-3 block">🌱</span>
                <p className="text-slate-400">No questions planted yet.</p>
                <p className="text-slate-500 text-sm">Be the first to plant a curiosity seed!</p>
              </div>
            )}
            
            {entries.map((entry) => (
              <div 
                key={entry.id}
                className="space-y-3"
              >
                {/* Child's question */}
                <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-xl">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-sm font-bold flex-shrink-0">
                      {entry.child_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-sm">{entry.child_name}</span>
                        <span className="text-green-400 text-xs">planted a question</span>
                      </div>
                      <p className="text-slate-300 mt-1">🌱 {entry.content}</p>
                    </div>
                  </div>
                </div>
                
                {/* Aret'Hera's response */}
                {entry.ai_response && (
                  <div className={`border p-4 rounded-xl ml-6 ${
                    entry.is_favorite 
                      ? 'bg-amber-500/10 border-amber-500/30' 
                      : 'bg-gradient-to-br from-amber-900/10 via-emerald-900/10 to-slate-900/20 border-amber-500/20'
                  }`}>
                    <div className="flex items-start gap-3 mb-2">
                      {/* Aret'Hera mini avatar */}
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500/40 to-emerald-500/20 flex items-center justify-center flex-shrink-0 border border-amber-400/30 shadow-[0_0_10px_rgba(251,191,36,0.2)]">
                        <div className="w-4 h-5 bg-gradient-to-b from-amber-300/80 to-amber-400/60 rounded-full relative">
                          <div className="absolute top-1.5 left-0.5 w-1 h-0.5 bg-slate-800/80 rounded-full"></div>
                          <div className="absolute top-1.5 right-0.5 w-1 h-0.5 bg-slate-800/80 rounded-full"></div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-amber-300 font-semibold text-sm">Aret'Hera</span>
                            <span className="text-emerald-500/60 text-xs">✧ wisdom grows</span>
                          </div>
                          {/* Favorite button */}
                          <button
                            onClick={() => handleToggleFavorite(entry.id)}
                            className={`p-1.5 rounded-lg transition-all ${
                              entry.is_favorite
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-slate-800/50 text-slate-500 hover:text-amber-400'
                            }`}
                            title={entry.is_favorite ? 'Remove from Garden of Wisdom' : 'Add to Garden of Wisdom'}
                            data-testid={`favorite-btn-${entry.id}`}
                          >
                            {entry.is_favorite ? '⭐' : '☆'}
                          </button>
                        </div>
                        <p className="text-slate-300 mt-1">{entry.ai_response}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <EmojiReactions 
                        emojis={emojis} 
                        reactions={entry.reactions || {}}
                        onReact={(emoji) => handleReact(entry.id, emoji)}
                      />
                      {entry.is_favorite && (
                        <span className="text-xs text-amber-400/70">✨ Saved to Wisdom Garden</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Ask a question input */}
          <div className="p-4 bg-slate-900/50 rounded-xl space-y-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <span className="text-lg">🌱</span>
              <span>Plant your question seed:</span>
            </div>
        
        <div className="space-y-3">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (question.trim().length >= 5 && !sending) {
                  handleAskQuestion();
                }
              }
            }}
            placeholder="What are you curious about? Ask Aret'Hera anything..."
            disabled={sending}
            maxLength={200}
            rows={2}
            className="w-full bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 resize-none disabled:opacity-50"
            data-testid="question-garden-input"
          />
          
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs">
              {question.length}/200 characters
            </span>
            
            <button
              onClick={handleAskQuestion}
              disabled={sending || question.trim().length < 5}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="question-garden-submit"
            >
              {sending ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-green-300/20 border-t-green-300 rounded-full"></div>
                  <span>Growing...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Plant Question</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Suggestion phrases */}
        <div className="pt-3 border-t border-slate-700/30">
          <p className="text-slate-500 text-xs mb-2">Need inspiration? Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {[
              "Why do stars twinkle?",
              "How do plants grow?",
              "What makes rainbows?",
              "Why is the sky blue?"
            ].map(suggestion => (
              <button
                key={suggestion}
                onClick={() => setQuestion(suggestion)}
                disabled={sending}
                className="px-2 py-1 text-xs bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 rounded-lg transition-all disabled:opacity-50"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
        </>
      )}

      {/* Garden of Wisdom Tab Content */}
      {activeTab === 'wisdom' && (
        <div className="space-y-6" data-testid="garden-of-wisdom">
          {/* Introduction */}
          <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">✨</span>
              </div>
              <div>
                <p className="text-amber-300 font-semibold">Your Garden of Wisdom</p>
                <p className="text-slate-400 text-sm mt-1">
                  Your favorite question seeds and the knowledge they grew into. These are the wisdoms you want to remember!
                </p>
              </div>
            </div>
          </div>

          {/* Wisdom Seeds Collection */}
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {wisdomSeeds.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-5xl mb-4 block">🌟</span>
                <p className="text-slate-400 text-lg mb-2">Your Garden of Wisdom is waiting</p>
                <p className="text-slate-500 text-sm">
                  Mark your favorite answers with ⭐ in the Question Garden to save them here!
                </p>
              </div>
            ) : (
              wisdomSeeds.map((seed, index) => (
                <div 
                  key={seed.id}
                  className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 p-5 rounded-xl"
                >
                  {/* Question */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <span>🌱</span>
                    </div>
                    <div>
                      <p className="text-xs text-green-400 mb-1">Question Seed #{index + 1}</p>
                      <p className="text-white">{seed.content}</p>
                    </div>
                  </div>

                  {/* Aret'Hera's Answer */}
                  <div className="flex items-start gap-3 ml-4 pl-4 border-l-2 border-amber-500/30">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <span>🌿</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-amber-400 mb-1">{seed.speaker || "Aret'Hera"}'s Wisdom</p>
                      <p className="text-slate-200">{seed.ai_response}</p>
                      
                      {/* Timestamp */}
                      <p className="text-xs text-slate-500 mt-2">
                        Saved {seed.favorited_at ? new Date(seed.favorited_at).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                    
                    {/* Remove from favorites */}
                    <button
                      onClick={() => handleToggleFavorite(seed.id)}
                      className="p-1.5 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all"
                      title="Remove from Garden of Wisdom"
                    >
                      ⭐
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Summary */}
          {wisdomSeeds.length > 0 && (
            <div className="text-center py-4 border-t border-slate-800/50">
              <p className="text-sm text-slate-500">
                🌟 {wisdomSeeds.length} wisdom seed{wisdomSeeds.length !== 1 ? 's' : ''} collected
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Celebration Hall Room Component
const CelebrationHallRoom = ({ childId, childName, emojis, phrases, onLeave }) => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedPhrase, setSelectedPhrase] = useState('');

  useEffect(() => {
    loadCelebrations();
    const interval = setInterval(loadCelebrations, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadCelebrations = async () => {
    try {
      const res = await recessAPI.getContributions('celebration_hall', 50);
      setContributions(res.data || []);
    } catch (error) {
      console.error('Error loading celebrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContribute = async (content) => {
    if (!content.trim()) return;
    
    setSending(true);
    try {
      const res = await recessAPI.contribute(childId, 'celebration_hall', 'celebration', content);
      if (res.data.warning) {
        toast.warning(res.data.warning);
      } else {
        toast.success('Celebration shared!');
        setSelectedPhrase('');
        loadCelebrations();
      }
    } catch (error) {
      toast.error('Could not share');
    } finally {
      setSending(false);
    }
  };

  const handleReact = async (contributionId, emoji) => {
    try {
      await recessAPI.react(contributionId, emoji);
      loadCelebrations();
    } catch (error) {
      console.error('Error reacting:', error);
    }
  };

  const celebrationPhrases = phrases.filter(p => 
    ['Great', 'Amazing', 'Well done', 'I finished', 'I learned', 'Wow', 'Cool'].some(w => p.startsWith(w))
  );

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-purple-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Loading Celebration Hall...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Celebrations list */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {contributions.length === 0 && (
          <div className="text-center py-8">
            <Trophy className="w-12 h-12 text-purple-400 mx-auto mb-3" />
            <p className="text-slate-400">No celebrations yet. Share your first achievement!</p>
          </div>
        )}
        
        {contributions.map((contribution) => (
          <div 
            key={contribution.id}
            className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl"
          >
            <div className="flex items-start gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                {contribution.child_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-sm">{contribution.child_name}</span>
                  <span className="text-purple-400 text-xs">celebrates</span>
                </div>
                <p className="text-slate-300 mt-1">{contribution.content}</p>
              </div>
            </div>
            <EmojiReactions 
              emojis={emojis} 
              reactions={contribution.reactions}
              onReact={(emoji) => handleReact(contribution.id, emoji)}
            />
          </div>
        ))}
      </div>

      {/* Share celebration */}
      <div className="p-4 bg-slate-900/50 rounded-xl space-y-3">
        <p className="text-slate-400 text-sm">Share your achievement:</p>
        
        <PhraseSelector 
          phrases={celebrationPhrases.length > 0 ? celebrationPhrases : phrases}
          onSelect={setSelectedPhrase}
          disabled={sending}
        />
        
        {selectedPhrase && (
          <div className="flex items-center gap-2">
            <p className="flex-1 text-cyan-300 bg-cyan-500/10 px-3 py-2 rounded-lg text-sm">
              "{selectedPhrase}"
            </p>
            <button
              onClick={() => handleContribute(selectedPhrase)}
              disabled={sending}
              className="p-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Recess Portal Page
const RecessPortalPage = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  
  const [rooms, setRooms] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [emojis, setEmojis] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);

  const selectedChild = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedChild') || 'null');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!selectedChild) {
      navigate('/child-select');
      return;
    }
    loadData();
  }, [selectedChild]);

  // Timer countdown
  useEffect(() => {
    if (!currentSession) return;
    
    const updateTimer = () => {
      const expires = new Date(currentSession.expires_at);
      const now = new Date();
      const diff = Math.max(0, Math.floor((expires - now) / 1000));
      
      if (diff <= 0) {
        setTimeLeft(0);
        toast.info('Your recess time has ended!');
        handleLeaveRoom();
      } else {
        setTimeLeft(diff);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [currentSession]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [roomsRes, phrasesRes, emojisRes, eligRes] = await Promise.all([
        recessAPI.getRooms(),
        recessAPI.getPhrases(),
        recessAPI.getEmojis(),
        recessAPI.checkEligibility(selectedChild.id)
      ]);
      
      setRooms(roomsRes.data || []);
      setPhrases(phrasesRes.data || []);
      setEmojis(emojisRes.data || []);
      setEligibility(eligRes.data);
      
      // If there's an active session from eligibility check
      if (eligRes.data?.active_session) {
        setCurrentSession(eligRes.data.active_session);
        const room = roomsRes.data?.find(r => r.id === eligRes.data.active_session.room_id);
        if (room) setCurrentRoom(room);
      }
      
      // If roomId is provided in URL, try to enter that room
      if (roomId && eligRes.data?.eligible && !eligRes.data?.active_session) {
        await handleEnterRoom(roomId);
      }
    } catch (error) {
      console.error('Error loading recess data:', error);
      toast.error('Failed to load Recess Portal');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterRoom = async (roomId) => {
    try {
      const res = await recessAPI.enterRoom(selectedChild.id, roomId);
      setCurrentSession({
        session_id: res.data.session_id,
        expires_at: res.data.expires_at,
        room_id: roomId
      });
      setCurrentRoom(res.data.room);
      toast.success(res.data.welcome_message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not enter room');
    }
  };

  const handleLeaveRoom = async () => {
    try {
      await recessAPI.leaveRoom(selectedChild.id);
      setCurrentSession(null);
      setCurrentRoom(null);
      loadData();
    } catch (error) {
      console.error('Error leaving room:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-2 border-white/20 border-t-cyan-500 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Opening Recess Portal...</p>
        </div>
      </div>
    );
  }

  // Not eligible screen
  if (!eligibility?.eligible && !currentSession) {
    return (
      <div className="min-h-screen bg-[#0F172A] py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/temple')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Temple</span>
          </button>

          <div className="glass-panel p-8 rounded-2xl text-center">
            <Lock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-philosopher font-bold text-white mb-4">
              Recess Portal Locked
            </h2>
            <p className="text-slate-400 mb-6">
              {eligibility?.message || 'Complete your daily quest to unlock recess!'}
            </p>
            <button
              onClick={() => navigate('/temple')}
              className="px-6 py-3 bg-cyan-500/20 text-cyan-400 rounded-xl hover:bg-cyan-500/30 transition-all"
            >
              Return to Temple
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Room selection screen
  if (!currentRoom) {
    return (
      <div className="min-h-screen bg-[#0F172A] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/temple')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Temple</span>
          </button>

          {/* Welcome message */}
          <div className="glass-panel p-6 rounded-2xl mb-8 bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-philosopher font-bold text-white">
                  Recess Portal
                </h1>
                <p className="text-slate-400">
                  Your learning path is honored, {selectedChild?.name}. Choose a chamber to explore!
                </p>
              </div>
            </div>
          </div>

          {/* Room selection */}
          <div className="grid md:grid-cols-3 gap-6">
            {rooms.map(room => {
              const IconComponent = ROOM_ICONS[room.icon] || BookOpen;
              const isAllowed = !eligibility?.allowed_rooms?.length || 
                               eligibility.allowed_rooms.includes(room.id);
              
              return (
                <button
                  key={room.id}
                  onClick={() => isAllowed && handleEnterRoom(room.id)}
                  disabled={!isAllowed}
                  className={`glass-panel p-6 rounded-2xl text-left transition-all ${
                    isAllowed 
                      ? 'hover:border-white/20 hover:scale-[1.02]' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  style={{ borderColor: isAllowed ? `${room.color}30` : undefined }}
                  data-testid={`recess-room-${room.id}`}
                >
                  <IconComponent 
                    className="w-10 h-10 mb-4" 
                    style={{ color: room.color }}
                  />
                  <h3 className="text-xl font-philosopher font-bold text-white mb-2">
                    {room.name}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {room.description}
                  </p>
                  {!isAllowed && (
                    <div className="mt-3 flex items-center gap-2 text-slate-500 text-xs">
                      <Lock className="w-3 h-3" />
                      <span>Not enabled by parent</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Inside a room
  const RoomIcon = ROOM_ICONS[currentRoom.icon] || BookOpen;

  return (
    <div className="min-h-screen bg-[#0F172A] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Room header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleLeaveRoom}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Leave Room</span>
          </button>
          
          {timeLeft !== null && (
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span className={timeLeft < 60 ? 'text-red-400' : ''}>
                {formatTime(timeLeft)} remaining
              </span>
            </div>
          )}
        </div>

        {/* Room title */}
        <div 
          className="glass-panel p-6 rounded-2xl mb-6"
          style={{ borderColor: `${currentRoom.color}30` }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${currentRoom.color}20` }}
            >
              <RoomIcon className="w-6 h-6" style={{ color: currentRoom.color }} />
            </div>
            <div>
              <h2 className="text-xl font-philosopher font-bold text-white">
                {currentRoom.name}
              </h2>
              <p className="text-slate-400 text-sm">{currentRoom.description}</p>
            </div>
          </div>
        </div>

        {/* Room content */}
        <div className="glass-panel p-6 rounded-2xl">
          {currentRoom.id === 'story_circle' && (
            <StoryCircleRoom 
              childId={selectedChild.id}
              childName={selectedChild.name}
              emojis={emojis}
              phrases={phrases}
              onLeave={handleLeaveRoom}
            />
          )}
          {currentRoom.id === 'question_garden' && (
            <QuestionGardenRoom 
              childId={selectedChild.id}
              childName={selectedChild.name}
              emojis={emojis}
              phrases={phrases}
              onLeave={handleLeaveRoom}
            />
          )}
          {currentRoom.id === 'celebration_hall' && (
            <CelebrationHallRoom 
              childId={selectedChild.id}
              childName={selectedChild.name}
              emojis={emojis}
              phrases={phrases}
              onLeave={handleLeaveRoom}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RecessPortalPage;
